import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { LeadTimelinePanel, SuggestedLeadMatch } from "./LeadTimelinePanel";
import LeadCompareHeader from "./LeadCompareHeader";
import { LeadCompareIdentity } from "../types";

// =============================================================================
// LEAD TIMELINE MODAL — thin portal/shell wrapper
// =============================================================================
// All state, data-fetching, and render logic lives in LeadTimelinePanel.
// This file ONLY owns:
//   - createPortal to document.body (escapes parent stacking contexts)
//   - Fixed-inset backdrop + backdrop-click close
//   - ESC key handler
//   - Focus trap
//   - role="dialog" aria-modal shell with detail-overlay sizing
//   - Unmounts <LeadTimelinePanel> when isOpen=false (free state reset)
//
// Public API is unchanged — all existing call sites are unaffected.
// =============================================================================

interface LeadTimelineModalProps {
  /** Lead email. Pass null (with anchorRecordingId) for orphan/transcript-only mode. */
  email: string | null;
  isOpen: boolean;
  onClose: () => void;
  /** Highlight a specific message in the DM panel (e.g., Calendly link) */
  highlightTimestamp?: string;
  /** Required when anchorRecordingId is set; optional otherwise */
  organizationId?: string;
  /** Open the Transcript tab pre-loaded with this recording */
  anchorRecordingId?: number;
  /** Scroll to and highlight this turn index within the transcript */
  anchorTurnIndex?: number;
  /** Org timezone for TranscriptPanel date display; defaults to 'UTC' */
  orgTz?: string;
  /** F5: Prior dialer info for the selected DialHQ lead — surfaced at the top of the timeline. */
  priorDialer?: { dialerId: string; name: string; attempts: number; lastAt: string };
}

const LeadTimelineModal: React.FC<LeadTimelineModalProps> = ({
  email,
  isOpen,
  onClose,
  highlightTimestamp,
  organizationId,
  anchorRecordingId,
  anchorTurnIndex,
  orgTz,
  priorDialer,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  // Step 6 (lead-phone-normalization compare): the modal is the shared owner of
  // both leads' emails + resolved identities. compareEmail null = single panel
  // (byte-identical to before); set = split view with the compare header.
  const [compareEmail, setCompareEmail] = useState<string | null>(null);
  const [rightSeed, setRightSeed] = useState<LeadCompareIdentity | null>(null);
  const [leftIdentity, setLeftIdentity] = useState<LeadCompareIdentity | null>(null);
  const [rightIdentity, setRightIdentity] = useState<LeadCompareIdentity | null>(null);

  // Both effects below are intentional resets of compare state on an external
  // change (anchor email, modal visibility) — the same idiom the codebase uses in
  // PatternReviewSection ("reset on filter change") and LostPreCallsModal ("reset
  // on close"). Reset compare when the anchor email changes (host navigated to
  // another lead); reset everything when the modal closes (state persists across
  // the null render since the component is not unmounted on !isOpen).
  /* eslint-disable react-hooks/set-state-in-effect -- intentional reset on email change / modal close */
  useEffect(() => {
    setCompareEmail(null);
    setRightSeed(null);
    setRightIdentity(null);
  }, [email]);

  useEffect(() => {
    if (!isOpen) {
      setCompareEmail(null);
      setRightSeed(null);
      setLeftIdentity(null);
      setRightIdentity(null);
    }
  }, [isOpen]);
  /* eslint-enable react-hooks/set-state-in-effect */

  // ESC key to close
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        // Prevent Esc from bubbling to outer page-level keydown listeners
        // (e.g. LeadSearchPage's "Esc clears search" handler on window).
        // A modal that consumes Esc owns the event.
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  // Focus trap
  useEffect(() => {
    if (!isOpen || !modalRef.current) return;
    const modal = modalRef.current;
    const focusable = modal.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last?.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first?.focus();
      }
    };

    modal.addEventListener("keydown", handleTab);
    first?.focus();
    return () => modal.removeEventListener("keydown", handleTab);
  }, [isOpen]);

  if (!isOpen) return null;

  // The anchor panel. Always wired with the two compare props (invisible until a
  // banner match exists). onOpenCompare seeds the right side + flips into split;
  // it stays on the left so clicking a different banner name re-points the right
  // (still 1:1). onIdentityResolved feeds the compare header the left lead's IG.
  const leftPanel = (
    <LeadTimelinePanel
      email={email}
      onClose={onClose}
      highlightTimestamp={highlightTimestamp}
      organizationId={organizationId}
      anchorRecordingId={anchorRecordingId}
      anchorTurnIndex={anchorTurnIndex}
      orgTz={orgTz}
      priorDialer={priorDialer}
      dialsLayout="column"
      railsCollapsedByDefault
      onOpenCompare={(m: SuggestedLeadMatch) => {
        setRightSeed({ email: m.email, name: m.name, phone: m.phone, instagram: null });
        setCompareEmail(m.email);
      }}
      onIdentityResolved={setLeftIdentity}
    />
  );

  // Use portal to render at document.body level, escaping any parent stacking contexts
  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      {/* Modal - wider for split panel */}
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="lead-timeline-modal-title"
        className="relative detail-overlay rounded-xl shadow-2xl w-full max-w-7xl max-h-[85vh] flex flex-col mx-4"
      >
        {/* Panel owns all state, data-fetching, and render.
            onClose is passed so the panel renders the X button.
            dialsLayout="column" = 3-column split (Timeline | DMs | Dials) —
            modal is wide enough; LostLeadsPage's narrow pane stays inline.
            railsCollapsedByDefault (Mako 2026-07-17): the modal now matches the
            LostLeadsPage triage pane — both rails start folded even WITH data, so
            the modal opens as a timeline-first glance and the rail COLOR is the
            signal (green = history in here, grey = empty). Click a rail to expand.
            Anchored-transcript mounts keep the DM rail open — see the panel's
            dmsCollapsed guard. */}
        {compareEmail === null ? (
          leftPanel
        ) : (
          <>
            <LeadCompareHeader
              left={leftIdentity}
              right={rightIdentity ?? rightSeed}
              onCloseCompare={() => setCompareEmail(null)}
            />
            {/* flex-1 is LOAD-BEARING: the panel root is h-full min-h-0, so the
                row must own a resolved height or both panels collapse to content
                height. Stacks below lg. */}
            <div className="flex flex-col lg:flex-row flex-1 min-h-0">
              <div className="flex-1 min-w-0 flex flex-col min-h-0 lg:border-r border-black/[0.08]">
                {leftPanel}
              </div>
              <div className="flex-1 min-w-0 flex flex-col min-h-0">
                <LeadTimelinePanel
                  email={compareEmail}
                  onClose={() => setCompareEmail(null)}
                  closeLabel="Close compare"
                  organizationId={organizationId}
                  orgTz={orgTz}
                  dialsLayout="column"
                  railsCollapsedByDefault
                  onIdentityResolved={setRightIdentity}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
};

export default LeadTimelineModal;
